// ============================================
// Path: /frontend/src/pages/MyInquiriesPage.jsx
// Purpose: Shows inquiries the current user has SENT
// ============================================

import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { inquiriesAPI } from '../api/inquiries.api';
import { useAuth } from '../hooks/useAuth';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { MessageSquare, Eye, Inbox, Mail, Phone, CheckCircle, Clock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// ========================================
// LOADING SKELETON COMPONENT
// ========================================
const LoadingSkeleton = () => (
  <div className="space-y-3 sm:space-y-4">
    {Array.from({ length: 3 }).map((_, i) => (
      <Card key={i}>
        <CardContent className="p-3 sm:p-4 md:p-6">
          <Skeleton className="h-5 sm:h-6 w-3/4 mb-2" />
          <Skeleton className="h-3 sm:h-4 w-1/2 mb-3 sm:mb-4" />
          <Skeleton className="h-16 sm:h-20 w-full" />
        </CardContent>
      </Card>
    ))}
  </div>
);

// ========================================
// MAIN COMPONENT
// ========================================
export default function MyInquiriesPage() {
  console.log('🎨 [MyInquiriesPage] Component mounted');

  const { user } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    const fetchMyInquiries = async () => {
      if (!user?.id) {
        console.log('⚠️ [MyInquiriesPage] No user ID found');
        setLoading(false);
        return;
      }

      console.log('🔵 [MyInquiriesPage] Fetching inquiries for user:', user.id);
      setLoading(true);

      try {
        const data = await inquiriesAPI.getUserInquiries(user.id);
        console.log('✅ [MyInquiriesPage] Inquiries received:', data);
        
        // Get the sent inquiries (inquiries this user sent)
        const sentInquiries = data.sent || data.inquiries || [];
        console.log('📤 [MyInquiriesPage] Sent inquiries:', sentInquiries);
        
        setInquiries(sentInquiries);
        setError(null);
      } catch (err) {
        console.error('❌ [MyInquiriesPage] Fetch error:', err);
        setError(err.response?.data?.message || 'Failed to load your inquiries');
      } finally {
        setLoading(false);
      }
    };

    fetchMyInquiries();
  }, [user]);

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleViewDetails = (inquiry) => {
    console.log('🔵 [MyInquiriesPage] Opening inquiry details:', inquiry.id);
    setSelectedInquiry(inquiry);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    console.log('🔵 [MyInquiriesPage] Closing inquiry details');
    setIsModalOpen(false);
    setSelectedInquiry(null);
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-4 sm:mb-6 md:mb-8">
            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-headline font-bold mb-1 sm:mb-2">
              My Inquiries
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
              Track all inquiries you've sent about listings
            </p>
          </div>

          {loading ? (
            <LoadingSkeleton />
          ) : error ? (
            <Card>
              <CardContent className="p-4 sm:p-6">
                <p className="text-xs sm:text-sm text-destructive">{error}</p>
              </CardContent>
            </Card>
          ) : inquiries.length === 0 ? (
            <Card>
              <CardContent className="p-6 sm:p-8 md:p-12 text-center">
                <Inbox className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-3 sm:mb-4 text-muted-foreground" />
                <h3 className="text-base sm:text-lg md:text-xl font-semibold mb-1 sm:mb-2">No Inquiries Yet</h3>
                <p className="text-xs sm:text-sm md:text-base text-muted-foreground mb-4 sm:mb-6">
                  You haven't sent any inquiries about listings yet.
                </p>
                <Link to="/listings">
                  <Button className="text-xs sm:text-sm h-9 sm:h-10">Browse Listings</Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {inquiries.map((inquiry) => (
                <Card key={inquiry.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-3 sm:p-4 md:p-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0 mb-3 sm:mb-4">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2 mb-1">
                          <h3 className="text-sm sm:text-base md:text-lg font-semibold">
                            {inquiry.listing?.title || inquiry.listingTitle || 'Listing'}
                          </h3>
                          {inquiry.sellerReply ? (
                            <Badge variant="default" className="bg-blue-600 w-fit text-[10px] sm:text-xs">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Seller Replied
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="w-fit text-[10px] sm:text-xs">
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </div>
                        <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground">
                          Sent on {inquiry.createdAt ? format(new Date(inquiry.createdAt), 'PPP') : 'N/A'}
                        </p>
                        {inquiry.sellerReply && inquiry.repliedAt && (
                          <p className="text-[10px] sm:text-xs md:text-sm text-blue-600 font-medium mt-1">
                            Reply received on {format(new Date(inquiry.repliedAt), 'PPP')}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant={inquiry.sellerReply ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleViewDetails(inquiry)}
                          className="text-xs sm:text-sm h-8 sm:h-9"
                        >
                          <MessageSquare className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                          {inquiry.sellerReply ? 'View Reply' : 'View Message'}
                        </Button>
                        <Link to={`/listing/${inquiry.listingId}`}>
                          <Button variant="outline" size="sm" className="text-xs sm:text-sm h-8 sm:h-9">
                            <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                            View Listing
                          </Button>
                        </Link>
                      </div>
                    </div>
                    
                    <div className="bg-muted p-2.5 sm:p-3 md:p-4 rounded-md mb-2">
                      <p className="text-[10px] sm:text-xs md:text-sm font-medium text-muted-foreground mb-1">Your Message:</p>
                      <p className="text-xs sm:text-sm line-clamp-2">
                        {inquiry.message || 'No message'}
                      </p>
                    </div>

                    {inquiry.sellerReply && (
                      <div className="bg-blue-50 border border-blue-200 p-2.5 sm:p-3 md:p-4 rounded-md">
                        <p className="text-[10px] sm:text-xs md:text-sm font-medium text-blue-700 mb-1">Seller's Reply:</p>
                        <p className="text-xs sm:text-sm text-gray-700 line-clamp-2">
                          {inquiry.sellerReply}
                        </p>
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-3 sm:mt-4 text-[10px] sm:text-xs md:text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="truncate">{inquiry.inquirerEmail}</span>
                      </div>
                      {inquiry.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span>{inquiry.phone}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />

      {/* INQUIRY DETAILS MODAL */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="space-y-1.5 sm:space-y-2">
            <DialogTitle className="text-base sm:text-lg md:text-xl">Inquiry Details</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Your inquiry and seller's response
            </DialogDescription>
          </DialogHeader>
          
          {selectedInquiry && (
            <div className="space-y-3 sm:space-y-4">
              <div>
                <h4 className="text-xs sm:text-sm font-semibold text-muted-foreground mb-1">Listing</h4>
                <p className="text-sm sm:text-base font-semibold">
                  {selectedInquiry.listing?.title || selectedInquiry.listingTitle || 'Listing'}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-muted-foreground mb-1 flex items-center gap-2">
                    <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
                    Your Email
                  </h4>
                  <p className="text-xs sm:text-sm md:text-base truncate">{selectedInquiry.inquirerEmail}</p>
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-muted-foreground mb-1 flex items-center gap-2">
                    <Phone className="h-3 w-3 sm:h-4 sm:w-4" />
                    Your Phone
                  </h4>
                  <p className="text-xs sm:text-sm md:text-base">{selectedInquiry.phone || 'Not provided'}</p>
                </div>
              </div>

              <div>
                <h4 className="text-xs sm:text-sm font-semibold text-muted-foreground mb-1">Date Sent</h4>
                <p className="text-xs sm:text-sm md:text-base">
                  {selectedInquiry.createdAt ? format(new Date(selectedInquiry.createdAt), 'PPP p') : 'N/A'}
                </p>
              </div>

              <div>
                <h4 className="text-xs sm:text-sm font-semibold text-muted-foreground mb-1">Your Message</h4>
                <div className="bg-muted p-3 sm:p-4 rounded-md">
                  <p className="text-xs sm:text-sm md:text-base whitespace-pre-wrap">
                    {selectedInquiry.message || 'No message provided'}
                  </p>
                </div>
              </div>

              {/* SELLER REPLY SECTION */}
              <div className="border-t pt-3 sm:pt-4">
                <h4 className="text-xs sm:text-sm font-semibold text-muted-foreground mb-1">Seller's Reply</h4>
                
                {selectedInquiry.sellerReply ? (
                  <div>
                    <div className="bg-blue-50 border border-blue-200 p-3 sm:p-4 rounded-md">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
                        <span className="text-[10px] sm:text-xs md:text-sm text-blue-700 font-medium">
                          Replied on {selectedInquiry.repliedAt ? format(new Date(selectedInquiry.repliedAt), 'PPP p') : 'N/A'}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm md:text-base whitespace-pre-wrap text-gray-700">
                        {selectedInquiry.sellerReply}
                      </p>
                    </div>
                    
                    {/* Contact seller buttons */}
                    <div className="flex flex-col sm:flex-row gap-2 mt-3 sm:mt-4">
                      {selectedInquiry.listing?.seller?.email && (
                        <a href={`mailto:${selectedInquiry.listing.seller.email}`} className="w-full sm:w-auto">
                          <Button variant="outline" size="sm" className="w-full text-xs sm:text-sm h-9 sm:h-10">
                            <Mail className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                            Email Seller
                          </Button>
                        </a>
                      )}
                      {selectedInquiry.listing?.seller?.phone && (
                        <a href={`tel:${selectedInquiry.listing.seller.phone}`} className="w-full sm:w-auto">
                          <Button variant="outline" size="sm" className="w-full text-xs sm:text-sm h-9 sm:h-10">
                            <Phone className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                            Call Seller
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-muted p-3 sm:p-4 rounded-md text-center">
                    <Clock className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Waiting for seller to reply...
                    </p>
                  </div>
                )}
              </div>

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-3 sm:pt-4 border-t">
                <Link to={`/listing/${selectedInquiry.listingId}`} className="w-full sm:w-auto">
                  <Button variant="outline" className="w-full text-xs sm:text-sm h-9 sm:h-10">
                    <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                    View Listing
                  </Button>
                </Link>
                <Button onClick={handleCloseModal} className="w-full sm:w-auto text-xs sm:text-sm h-9 sm:h-10">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}